import { isPhoneApp } from "@/lib/phoneApp";
import { COLORS, Wordmark } from "@/lib/theme";
import { useAuth } from "@/lib/auth";
import { useIsDesktop } from "@/lib/media";
import Footer from "@/components/Footer";

export default function Pending() {
  const { user, logout } = useAuth();
  const isDesktop = useIsDesktop();

  const ClockIcon = (
    <div style={{ width: 84, height: 84, borderRadius: "50%", background: COLORS.lilac, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke={COLORS.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    </div>
  );

  const InfoCard = (
    <div className="ls-card" style={{ padding: 16, marginTop: 4, width: "100%", maxWidth: 380 }}>
      <div style={{ fontSize: 12, color: COLORS.muted, fontWeight: 700, letterSpacing: 0.6 }}>REGISTERED AS</div>
      <div style={{ fontWeight: 800, fontSize: 15, marginTop: 4 }}>{user?.first_name} {user?.last_name}</div>
      <div style={{ fontSize: 13, color: COLORS.purple, fontWeight: 700, marginTop: 2 }}>{user?.email}</div>
    </div>
  );

  if (isDesktop) {
    return (
      <div data-testid="pending-screen" style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: COLORS.cream }}>
        <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1.1fr 1fr", minHeight: "80vh" }}>
          <div style={{ background: `linear-gradient(160deg, ${COLORS.purple} 0%, ${COLORS.plum} 100%)`, color: "#fff", padding: "80px 60px", display: "flex", flexDirection: "column", justifyContent: "center", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: -80, right: -80, width: 320, height: 320, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
            <div style={{ position: "absolute", bottom: -60, left: -60, width: 220, height: 220, borderRadius: "50%", background: "rgba(201,162,75,0.14)" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 32, position: "relative", zIndex: 1 }}>
              <img src="/assets/linkshul-logo.png" alt="LinkShul" style={{ width: 110, borderRadius: 18, boxShadow: "0 20px 50px rgba(0,0,0,0.35)" }} />
              <div>
                <div className="font-heading" style={{ fontWeight: 800, fontSize: 42, letterSpacing: "-1px", lineHeight: 1 }}>
                  Link<span style={{ color: COLORS.goldLight }}>shul</span>
                </div>
                <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 14, fontWeight: 700, marginTop: 6 }}>
                  {isPhoneApp ? "LinkShul" : "LinkShul"}
                </div>
              </div>
            </div>
            <h1 className="font-heading" style={{ fontWeight: 800, fontSize: 40, lineHeight: 1.15, marginBottom: 14, position: "relative", zIndex: 1 }}>
              Almost there, {user?.first_name || "friend"}.
            </h1>
            <p style={{ fontSize: 17, lineHeight: 1.65, color: "rgba(255,255,255,0.85)", maxWidth: 520, fontWeight: 500, position: "relative", zIndex: 1 }}>
              Thank you for registering with LinkShul. To keep our community secure, every new
              account is reviewed by an administrator before it&apos;s activated.
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 40px" }}>
            <div style={{ width: "100%", maxWidth: 440, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 20 }}>
              {ClockIcon}
              <div className="font-heading" style={{ fontWeight: 800, fontSize: 28, color: COLORS.ink }}>Awaiting approval</div>
              <div style={{ color: COLORS.muted, fontWeight: 600, fontSize: 15, lineHeight: 1.65 }}>
                Shalom {user?.first_name}. Your registration has been received and is being reviewed by an administrator.
                You&apos;ll be notified once your account is approved.
              </div>
              {InfoCard}
              <div style={{ fontSize: 13, color: COLORS.muted, fontWeight: 600, lineHeight: 1.55, maxWidth: 380 }}>
                For assistance, contact the office on <a href="mailto:Office@linkshul.co.za" style={{ color: COLORS.purple, fontWeight: 800 }}>Office@linkshul.co.za</a> or 011 640 5812.
              </div>
              <button data-testid="pending-logout" onClick={logout} className="ls-outline-btn" style={{ marginTop: 6 }}>Sign out</button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Mobile layout (unchanged)
  return (
    <div className="ls-shell" data-testid="pending-screen" style={{ display: "flex", flexDirection: "column" }}>
      <div style={{
        background: `linear-gradient(160deg, ${COLORS.purple} 0%, ${COLORS.plum} 100%)`,
        padding: "44px 24px 30px", borderRadius: "0 0 32px 32px",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
      }}>
        <img src="/assets/linkshul-logo.png" alt="LinkShul" style={{ width: 100, borderRadius: 14, boxShadow: "0 12px 30px rgba(0,0,0,0.35)" }} />
        <Wordmark scale={0.9} light />
      </div>
      <div style={{ flex: 1, padding: "36px 26px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 20 }}>
        {ClockIcon}
        <div className="font-heading" style={{ fontWeight: 800, fontSize: 24, color: COLORS.ink }}>Awaiting approval</div>
        <div style={{ color: COLORS.muted, fontWeight: 600, fontSize: 14.5, lineHeight: 1.6, maxWidth: 320 }}>
          Shalom {user?.first_name}. Your registration has been received and is being reviewed by an administrator.
          You&apos;ll be notified once your account is approved.
        </div>
        {InfoCard}
        <div style={{ fontSize: 12.5, color: COLORS.muted, fontWeight: 600, lineHeight: 1.5, maxWidth: 320 }}>
          For assistance, contact the office on <a href="mailto:Office@linkshul.co.za" style={{ color: COLORS.purple, fontWeight: 800 }}>Office@linkshul.co.za</a> or 011 640 5812.
        </div>
        <button data-testid="pending-logout" onClick={logout} className="ls-outline-btn" style={{ marginTop: 14 }}>Sign out</button>
      </div>
    </div>
  );
}
